import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { PagedResult } from '../models/paging';
import {
  CreateOrganizationRequest,
  DownloadAccessGroupReportRequest,
  DownloadMultipleOrganizationReportRequest,
  DownloadReportRequest,
  EditOrganizationRequest,
  Organization,
  OrganizationSearchEvent,
  UpdateOrganizationDiscountTypeRequest,
  OrganizationKickbackResponse,
  CreateKickbackRequest,
  EditKickbackRequest,
} from 'src/app/features/organization/organization-models';
import { Department } from 'src/app/features/department/department.model';
import { Sort } from '@angular/material/sort';
import { LocalStorageDataService } from './local-storage.service';
import { PageEvent } from '@angular/material/paginator';

const organizationStorageKey = 'organizations';
const organizationStorageTimestampKey = 'organizationStorageTimestamp';
const dataCheckInterval: number = 12 * 60 * 60 * 1000; // 12 hours in miliseconds
@Injectable({
  providedIn: 'root',
})
export default class OrganizationService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private localStorageService = inject(LocalStorageDataService);
  private accessGroupReportV2 = this.environmentLoaderService.config.locationAccessGroupReportV2;

  apiUrl: string;
  apiUrlV2: string;

  constructor() {
    this.apiUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/organization';
    this.apiUrlV2 = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v2/admin/organization';
  }

  public getOrganizations(searchEvent?: OrganizationSearchEvent): Observable<PagedResult<Organization>> {
    const url = `${this.apiUrl}`;
    let params = new HttpParams();
    const paging = searchEvent?.paging;
    const sorting = searchEvent?.sorting;

    if (paging?.pageIndex) {
      params = params.append('page', paging.pageIndex);
    }
    if (paging?.pageSize) {
      params = params.append('pageSize', paging.pageSize);
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (sorting?.active) {
      params = params.append('sortby', sorting.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting.direction);
    }

    return this.http.get<PagedResult<Organization>>(url, {
      params,
    });
  }

  public getOrganization(organizationId: number): Observable<Organization> {
    return this.http.get<Organization>(`${this.apiUrl}/${organizationId}`);
  }

  public getDepartmentsForOrganization(organizationId: number, sorting: Sort): Observable<Array<Department>> {
    const url = `${this.apiUrl}/${organizationId}/Departments`;
    let params = new HttpParams();

    if (sorting?.active) {
      params = params.append('sortByProperty', sorting?.active);
    }
    if (sorting?.direction) {
      params = params.append('sortdirection', sorting?.direction);
    }

    return this.http.get<Array<Department>>(url, {
      params,
    });
  }

  public createOrganization(request: CreateOrganizationRequest): Observable<any> {
    return this.http.post(`${this.apiUrl}`, request);
  }
  public editOrganization(organizationId: number, request: EditOrganizationRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/${organizationId}`, request);
  }

  public deleteOrganization(organizationId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${organizationId}`);
  }
  public downloadInvoiceReport(downloadReportModel: DownloadReportRequest): Observable<Blob> {
    const url = `${this.apiUrl}/InvoiceReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }

  public downloadConsumptionReport(downloadReportModel: DownloadReportRequest): Observable<Blob> {
    const url = `${this.apiUrl}/ConsumptionReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }

  public downloadLocationReport(downloadReportModel: DownloadReportRequest): Observable<Blob> {
    const url = `${this.apiUrl}/LocationReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }
  public downloadMultipleOrganizationReport(downloadReportModel: DownloadMultipleOrganizationReportRequest): Observable<Blob> {
    const url = `${this.apiUrl}/MultipleOrganizationLocationReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }
  public downloadAccessGroupReport(downloadReportModel: DownloadAccessGroupReportRequest): Observable<Blob> {
    const url = this.accessGroupReportV2 === 'true' ? `${this.apiUrlV2}/accessgroup-report` : `${this.apiUrl}/accessgroup-report`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }

  public downloadInvoiceForLocationReport(downloadReportModel: DownloadReportRequest): Observable<Blob> {
    const url = `${this.apiUrl}/InvoiceForLocationsReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }

  public downloadSummarizedInvoiceReport(downloadReportModel: DownloadReportRequest): Observable<Blob> {
    const url = `${this.apiUrl}/SummarizedInvoiceReport`;
    return this.http.post<Blob>(url, downloadReportModel, {
      responseType: 'blob' as 'json',
    });
  }

  public updateDiscountType(updateDiscountTypeRequest: UpdateOrganizationDiscountTypeRequest): Observable<void> {
    const url = `${this.apiUrl}/DiscountType`;
    return this.http.post<void>(url, updateDiscountTypeRequest);
  }

  public async getAndStoreOrganizations(): Promise<Organization[]> {
    const organizationStorageTimestamp: string = this.localStorageService.get(organizationStorageTimestampKey);
    let refreshOrganizationsInStorage = true;
    if (organizationStorageTimestamp) {
      refreshOrganizationsInStorage = new Date(organizationStorageTimestamp).getTime() + dataCheckInterval < Date.now();
    }

    let organizations: Organization[] = this.localStorageService.get(organizationStorageKey);

    if (!organizations || organizations.length === 0) {
      refreshOrganizationsInStorage = true;
    }

    if (refreshOrganizationsInStorage) {
      const searchEvent: OrganizationSearchEvent = {
        paging: new PageEvent(),
        sorting: null,
      };
      searchEvent.paging.pageIndex = 0;
      searchEvent.paging.pageSize = 10000;
      organizations = (await firstValueFrom(this.getOrganizations(searchEvent))).items;
      const minimalOrganizations = organizations.map(({ id, name }) => ({ id, name }));
      this.localStorageService.set(organizationStorageKey, minimalOrganizations);
      this.localStorageService.set(organizationStorageTimestampKey, new Date().toISOString());
    }

    return organizations;
  }

  public getKickbacks(organizationId: number): Observable<OrganizationKickbackResponse> {
    const url = `${this.apiUrl}/${organizationId}/kickbacks`;
    return this.http.get<OrganizationKickbackResponse>(url);
  }

  public createKickback(organizationId: number, request: CreateKickbackRequest): Observable<void> {
    const url = `${this.apiUrl}/${organizationId}/kickbacks`;
    return this.http.post<void>(url, request);
  }

  public deleteKickback(organizationId: number, kickbackId: number): Observable<void> {
    const url = `${this.apiUrl}/${organizationId}/kickbacks/${kickbackId}`;
    return this.http.delete<void>(url);
  }

  public editKickback(organizationId: number, kickbackId: number, request: EditKickbackRequest): Observable<void> {
    const url = `${this.apiUrl}/${organizationId}/kickbacks/${kickbackId}`;
    return this.http.put<void>(url, request);
  }
}
